import React, { CSSProperties, useEffect } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useCollection } from 'react-firebase-hooks/firestore';
import { SxStyle } from '../../../types';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import ListItemIcon from '@mui/material/ListItemIcon';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import TextsmsIcon from '@mui/icons-material/Textsms';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Divider from '@mui/material/Divider';
import firebaseApp from '../../../firebase-app';
import { useStringsContext } from '../../../contexts/strings';
import { collection } from 'firebase/firestore';

const styles: SxStyle = {
  root: {
    width: '100%',
    position: 'relative',
    overflow: 'auto',
    minHeight: '70vh',
    maxHeight: '70vh',
  },
  nested: (theme) => ({
    paddingLeft: theme.spacing(4),
  }),
  listHeader: (theme) => ({
    color: 'inherit',
    textAlign: 'center',
    fontSize: theme.typography.pxToRem(15),
    backgroundColor: theme.palette.background.default,
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
  }),
  listIcons: (theme) => ({
    color: theme.palette.primary.main,
  }),
  logo: {
    width: '30px',
    height: '30px',
    objectFit: 'contain',
    position: 'relative',
    border: 0,
    '&:after': {
      content: '"" attr(alt)',
      position: 'absolute',
      border: 0,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'white',
    },
  },
};

export default function FilterList({
  setCurrentAppFilter,
}: InferProps<typeof FilterList.propTypes>) {
  const { strings } = useStringsContext();
  const [checked, setChecked] = React.useState(0);
  const [teams, setTeams] = React.useState<any[]>([]);
  const [open, setOpen] = React.useState(true);
  const [messageTypeFilterOpen, setMessageTypeFilterOpen] = React.useState(true);

  const [teamsRef, loading, error] = useCollection(collection(firebaseApp.db, `/teams`));

  useEffect(() => {
    if (teamsRef?.docs && !loading && !error && teams.length === 0) {
      const newTeams = teamsRef.docs.map((doc: any) => doc.data());
      if (newTeams) {
        setTeams(newTeams);
        setCurrentAppFilter(newTeams[0].abbrv);
      }
    }
  }, [error, loading, setCurrentAppFilter, teams, teamsRef?.docs]);

  const handleClickMessageTypeFilter = () => {
    setMessageTypeFilterOpen(!messageTypeFilterOpen);
  };

  const handleClick = () => {
    setOpen(!open);
  };

  const handleToggle = (value: number, abbrv: string) => () => {
    setChecked(value);
    setCurrentAppFilter(abbrv);
  };

  return (
    <List
      component="nav"
      subheader={
        <ListSubheader sx={styles.listHeader} component="div" id="nested-list-subheader">
          {strings.messagesViewer.filterMessages}
        </ListSubheader>
      }
      sx={styles.root}
    >
      <ListItemButton onClick={handleClickMessageTypeFilter}>
        <ListItemIcon>
          <TextsmsIcon sx={styles.listIcons} />
        </ListItemIcon>
        <ListItemText primary={strings.messagesViewer.selectMessageType} />
        {messageTypeFilterOpen ? (
          <ExpandLess sx={styles.listIcons} />
        ) : (
          <ExpandMore sx={styles.listIcons} />
        )}
      </ListItemButton>
      <Collapse in={messageTypeFilterOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItemButton onClick={handleClick}>
            <FormControl component="fieldset">
              <RadioGroup
                aria-label="messageType"
                name="messageType"
                value={'valuestats'}
                // onChange={handleChange}
              >
                <FormControlLabel
                  value="valuestats"
                  control={<Radio />}
                  label={strings.messagesViewer.unitbetValuestats}
                  labelPlacement="start"
                />
                <FormControlLabel
                  disabled
                  value="upcoming1"
                  control={<Radio />}
                  label={strings.messagesViewer.upcomingMessageType}
                  labelPlacement="start"
                />
              </RadioGroup>
            </FormControl>
          </ListItemButton>
        </List>
      </Collapse>
      <Divider />
      <ListItemButton onClick={handleClick}>
        <ListItemIcon>
          <PhoneIphoneIcon sx={styles.listIcons} />
        </ListItemIcon>
        <ListItemText primary={strings.messagesViewer.filterByApp} />
        {open ? <ExpandLess sx={styles.listIcons} /> : <ExpandMore sx={styles.listIcons} />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {teams.map((team, index: number) => {
            return (
              <ListItem
                key={index}
                secondaryAction={
                  <Radio
                    edge="end"
                    onChange={handleToggle(index, team.abbrv)}
                    checked={checked === index}
                    inputProps={{ 'aria-labelledby': team.abbrv }}
                  />
                }
              >
                <ListItemIcon>
                  {/* TODO Chnage to Image component */}
                  <img
                    style={styles.logo as CSSProperties}
                    src={team.logoImageUrl}
                    height="30px"
                    alt=""
                  />
                </ListItemIcon>
                <ListItemText id={team.abbrv} primary={team.name} />
              </ListItem>
            );
          })}
        </List>
      </Collapse>
    </List>
  );
}

FilterList.propTypes = {
  setCurrentAppFilter: PropTypes.func.isRequired,
};
